/**
 * @fileoverview
 * In-app notification store - list and unread count for the header bell.
 * Separate from toast NotificationStore.
 *
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable, signal, computed } from '@angular/core';
import { InAppNotificationItem } from '../models/in-app-notification.model';

export interface NotificationPaginationMeta {
  page: number;
  totalPages: number;
}

@Injectable({
  providedIn: 'root',
})
export class InAppNotificationStore {
  private readonly itemsSignal = signal<InAppNotificationItem[]>([]);
  private readonly paginationMetaSignal = signal<NotificationPaginationMeta | null>(null);

  readonly items = this.itemsSignal.asReadonly();
  readonly unreadCount = computed(
    () => this.itemsSignal().filter((i) => i.unread).length
  );
  readonly hasUnread = computed(() => this.unreadCount() > 0);
  readonly paginationMeta = this.paginationMetaSignal.asReadonly();
  readonly hasMoreNotifications = computed(() => {
    const meta = this.paginationMetaSignal();
    return meta !== null && meta !== undefined && meta.page < meta.totalPages;
  });
  readonly currentNotificationPage = computed(() => this.paginationMetaSignal()?.page ?? 1);

  add(item: InAppNotificationItem): void {
    this.itemsSignal.update((list) => [item, ...list]);
  }

  markAsRead(id: string): void {
    this.itemsSignal.update((list) =>
      list.map((i) => (i.id === id ? { ...i, unread: false } : i))
    );
  }

  markAllAsRead(): void {
    this.itemsSignal.update((list) =>
      list.map((i) => ({ ...i, unread: false }))
    );
  }

  clear(): void {
    this.itemsSignal.set([]);
    this.paginationMetaSignal.set(null);
  }

  clearRead(): void {
    this.itemsSignal.update((list) => list.filter((i) => i.unread));
  }

  /**
   * Set list (e.g. from API history). Replaces current list.
   */
  setItems(items: InAppNotificationItem[]): void {
    this.itemsSignal.set(items);
  }

  /**
   * Set pagination meta from API response (for lazy load state).
   */
  setPaginationMeta(meta: NotificationPaginationMeta): void {
    this.paginationMetaSignal.set(meta);
  }

  /**
   * Prepend items (e.g. from API history merge). Dedupe by id.
   */
  prependItems(items: InAppNotificationItem[]): void {
    const existingIds = new Set(this.itemsSignal().map((i) => i.id));
    const newItems = items.filter((i) => !existingIds.has(i.id));
    if (newItems.length === 0) return;
    this.itemsSignal.update((list) => [...newItems, ...list]);
  }

  /**
   * Append items (e.g. next page for lazy load). Dedupe by id.
   */
  appendItems(items: InAppNotificationItem[]): void {
    const existingIds = new Set(this.itemsSignal().map((i) => i.id));
    const newItems = items.filter((i) => !existingIds.has(i.id));
    if (newItems.length === 0) return;
    this.itemsSignal.update((list) => [...list, ...newItems]);
  }
}
